/** Generic DocType Detail Page — schema-driven CRUD with Experience/System views */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookingDetailContent } from "@/features/booking/components/BookingDetailContent";
import { BookingResourceConfigDetailContent } from "@/features/booking/components/BookingResourceConfigDetailContent";
import { toast } from "sonner";
import { PageContainer } from "@/components/layout/PageContainer";
import { DetailView } from "@/components/layout/DetailView";
import { DetailPageTabBar } from "@/components/layout/DetailPageTabBar";
import { useGetDoc, useCreateDoc, useUpdateDoc, useDeleteDoc } from "@/lib/hooks/useFrappeQuery";
import { useDocSidebar } from "@/features/common/api/useDocSidebar";
import { DocChatPanel } from "@/features/common/components/DocChatPanel";
import { InlineAssignments } from "@/features/common/components/InlineAssignments";
import { InlineShareActions } from "@/features/common/components/InlineShareActions";
import { AttachmentListModal } from "@/features/common/components/AttachmentListModal";
import { TagListModal } from "@/features/common/components/TagListModal";
import { HeaderMediaThumbnail } from "@/features/inventory/item-detail/components/HeaderMediaThumbnail";
import { HeaderActions } from "@/features/inventory/item-detail/components/HeaderActions";
import { DocStatusBadge } from "@/components/ui/doc-status-badge";
import { useDocWorkflowHandlers } from "@/hooks/useDocWorkflowHandlers";
import { captureProductAdded } from "@/lib/analytics/posthog-events";
import { useDetailPageTabs, DetailPageTabsProvider } from "@/contexts/DetailPageTabsContext";
import { useDetailPageChat } from "@/contexts/DetailPageChatContext";
import { erp } from "@/lib/erp";
import { isDirty } from "@/features/inventory/item-detail/utils/computeDirtyState";
import { useAuthStore } from "@/lib/store/authStore";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { cn, safeDecodeURIComponent } from "@/lib/utils";
import { prepareDocForSave } from "@/lib/erp/save";
import { pruneLocalPlaceholderChildRowsInDoc } from "@/components/child-table/utils/prune";
import { validateDocForm } from "./validateDocForm";
import { resolveView } from "@/lib/views/registry";
import { SystemView } from "@/components/view/SystemView";
import { ExperienceView } from "@/components/view/ExperienceView";
import type { DocTypeSchema } from "@/lib/erp/meta";
import type { OrderContext } from "@/lib/views/types";
import { createSyntheticDocTypeView } from "@/lib/views/utils/createSyntheticDocTypeView";
import type { StandardDetailPageProps } from "./StandardDetailPage.types";
import { hasDocViewSchema, getDocViewSchema } from "@/lib/views/docViewSchemas";
import {
  buildDetailRoute,
  buildListRoute,
  decodeAndNormalizeDoctype,
} from "@/lib/config/docRoutes";
import { useCanonicalDocRedirect } from "./useCanonicalDocRedirect";
import { useDetailChatEffects } from "./useDetailChatEffects";
import { useCanQuery } from "@/hooks/useCanQuery";
import { useRegisterActiveDetailPage } from "@/hooks/useRegisterActiveDetailPage";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DetailPageSkeleton } from "./DetailPageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/lib/utils/logger";
import { buildNewDocDefaults } from "./buildNewDocDefaults";
import { ChevronLeft } from "lucide-react";
import { DocLoadErrorCard } from "./DocLoadErrorCard";
import { getDocTitle, areRequiredFieldsFilled } from "@/lib/erp/docTitle";
import { useRenameHandler } from "./useRenameHandler";
import { RenameConfirmDialog } from "./RenameConfirmDialog";
import { GenericDetailContent } from "@/features/list/GenericDetailContent";
import { DetailStickyTopBar } from "@/components/layout/DetailStickyTopBar";

export function StandardDetailPage({ forcedDoctype, renderRightSidebar, renderBelowMain, renderAboveMain, headerMedia, headerSubtitle, headerMediaLayout = "hero" }: StandardDetailPageProps) {
  const params = useParams<{ doctype?: string; name?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const doctype = params.doctype
    ? decodeAndNormalizeDoctype(params.doctype)
    : decodeAndNormalizeDoctype(forcedDoctype);
  const isNew = params.name === undefined;
  const docName = !isNew && params.name ? safeDecodeURIComponent(params.name) : "";

  // Redirect generic routes to canonical (mapped) routes
  useCanonicalDocRedirect({ doctype, docName, isNew, location, searchParams, navigate });

  // Chat panel state
  const { showChat, setShowChat, setOnChatClick } = useDetailPageChat();
  const { activeTab } = useDetailPageTabs();
  const hasInitializedFromUrlRef = useRef(false);

  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Chat click handler, close-on-tab-change, body scroll lock
  useDetailChatEffects({ showChat, setShowChat, setOnChatClick, activeTab });

  // Auth state
  const authStore = useAuthStore();
  const { isAuthenticated } = authStore;
  const queryClient = useQueryClient();
  const [uiError, setUiError] = useState<string | null>(null);
  
  // Track document version for child table resync
  const [docVersion, setDocVersion] = useState(0);

  // Fetch document data (if not new)
  const { data: docData, isLoading: isLoadingDoc, error: docError } = useGetDoc(
    doctype,
    docName,
    { enabled: !isNew && !!docName && isAuthenticated }
  );
  
  // Fetch sidebar data (only for existing documents)
  const sidebarQuery = useDocSidebar(doctype, isNew ? undefined : docName);
  
  // Fetch meta schema
  const { data: metaSchema, isLoading: isLoadingMeta } = useQuery({
    queryKey: ["meta", doctype],
    queryFn: () => erp.meta.getDocTypeSchema(doctype),
    enabled: isAuthenticated && !!doctype,
  });
  
  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});

  // Register the open record (dev autofill + page-aware AI). In ?generic=1 mode the
  // child GenericDetailContent registers it instead (it owns the nested tabs provider).
  useRegisterActiveDetailPage({
    doctype,
    metaSchema,
    formData,
    setFormData,
    savedData: originalData,
    enabled: !(searchParams.get("generic") === "1" && docName),
  });
  
  // Refs to track hydration state
  const hasHydratedRef = useRef(false);
  const currentDocRef = useRef<string>("");
  const isSavingRef = useRef(false);
  
  // Initialize form data
  useEffect(() => {
    if (currentDocRef.current !== docName) {
      currentDocRef.current = docName;
      hasHydratedRef.current = false;
      hasInitializedFromUrlRef.current = false;
    }
    
    if (docData) {
      if (!hasHydratedRef.current) {
        setFormData(docData);
        setOriginalData(docData);
        setDocVersion(0);
        hasHydratedRef.current = true;
        return;
      }
      
      if (isSavingRef.current) {
        return;
      }

      const isDirtyNow = isDirty(originalData, formData);
      if (isDirtyNow) {
        return;
      }
    } else if (isNew && metaSchema) {
      // Only apply URL defaults once
      if (hasInitializedFromUrlRef.current) return;
      
      const defaults = buildNewDocDefaults(metaSchema, doctype, searchParams, authStore.tenantCompany);

      setFormData(defaults);
      setOriginalData(defaults);
      setDocVersion(0);
      hasInitializedFromUrlRef.current = true;
    }
  }, [docData, isNew, metaSchema, docName, originalData, formData, searchParams]);
  
  // Compute resetKey for child tables
  const resetKey = isNew ? `new-${docVersion}` : `${docName}-${docVersion}`;
  
  // View resolution
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const devMode = usePreferencesStore((state) => state.devMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);
  
  const context: OrderContext | undefined = undefined;
  const systemMode = devMode ? viewMode === 'system' : false;
  
  // Resolve view - handle schema-only doctypes
  const resolvedView = useMemo(() => {
    if (!metaSchema) return null;
    
    const preferredStyle = !systemMode && experienceStyle !== 'auto' 
      ? experienceStyle 
      : undefined;
    
    // Try standard resolution first
    const view = resolveView(
      metaSchema.name,
      context,
      systemMode,
      devMode,
      preferredStyle
    );
    
    // If user hasn't forced system mode, and no registered view exists but schema exists,
    // create synthetic view to enable ExperienceView rendering
    if (!systemMode && view.mode === 'system' && hasDocViewSchema(doctype)) {
      const schema = getDocViewSchema(doctype);
      const syntheticView = createSyntheticDocTypeView(doctype, schema);
      
      // Return experience mode with synthetic view
      return {
        mode: 'experience' as const,
        view: syntheticView,
        style: syntheticView.style,
      };
    }
    
    return view;
  }, [metaSchema, doctype, context, systemMode, devMode, experienceStyle]);
  
  // Permissions
  const { allowed: canWrite } = useCanQuery(
    doctype,
    "write",
    isNew ? undefined : docName
  );
  const { allowed: canDelete } = useCanQuery(
    doctype,
    "delete",
    isNew ? undefined : docName
  );
  
  // Mutations
  const createMutation = useCreateDoc<Record<string, any>>(doctype, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
    },
  });

  const safeUpdateName = isNew ? "__new__" : docName;

  const updateMutation = useUpdateDoc<Record<string, any>>(doctype, safeUpdateName, {
    onSuccess: () => {
      if (!isNew && docName) {
        queryClient.invalidateQueries({ queryKey: ["get", doctype, docName] });
      }
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
    },
  });
  
  const deleteMutation = useDeleteDoc(doctype, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
      navigate(buildListRoute(doctype));
    },
    onError: (error: Error) => {
      const cleanMessage = error.message.replace(/<[^>]*>/g, "").trim();
      toast.error(cleanMessage || "Failed to delete record", { duration: 8000 });
    },
  });
  
  // Document workflow (submit/cancel/amend) for submittable doctypes
  const workflow = useDocWorkflowHandlers({
    doctype,
    name: docName,
    formData,
    isSubmittable: !!metaSchema?.is_submittable,
    permissions: metaSchema?.permissions,
    onMutationSuccess: () => { hasHydratedRef.current = false; },
  });
  const isReadOnly = !isNew && (formData.docstatus ?? 0) >= 1;

  // Rename handler — intercepts save when autoname field is edited
  const rename = useRenameHandler({ doctype: doctype ?? "", docName, isNew, metaSchema });

  // For non-renameable DocTypes, force the autoname field to read-only on existing docs.
  // Only used for view rendering — validation/save/rename detection keep original metaSchema.
  const effectiveMetaSchema = useMemo(() => {
    if (!metaSchema) return metaSchema;
    const autoname = metaSchema.autoname;
    if (!autoname || !autoname.startsWith("field:")) return metaSchema;
    // If allow_rename is truthy, field stays editable (user can rename)
    if (metaSchema.allow_rename) return metaSchema;
    // New docs: naming field must be editable to set the initial name
    if (isNew) return metaSchema;
    const fieldname = autoname.split(":")[1];
    if (!fieldname) return metaSchema;
    return {
      ...metaSchema,
      fields: metaSchema.fields.map((f: any) =>
        f.fieldname === fieldname ? { ...f, read_only: 1 } : f
      ),
    } as DocTypeSchema;
  }, [metaSchema, isNew]);

  // Compute dirty state
  const dirtyState = useMemo(() => {
    if (isNew) {
      return Object.values(formData).some(
        (val) => val !== null && val !== undefined && val !== ""
      );
    }
    return isDirty(originalData, formData);
  }, [formData, originalData, isNew]);

  // Required field check for Create button (new docs only).
  // Mirrors ItemDetailContent.tsx — without this, HeaderActions receives
  // allRequiredFilled=undefined and the Create button stays disabled forever.
  const allRequiredFilled = useMemo(
    () => !metaSchema || !isNew || areRequiredFieldsFilled(metaSchema, formData),
    [formData, metaSchema, isNew],
  );

  const handleFieldChange = (fieldname: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
  };
  
  // Validate form before save
  const validateForm = useCallback(async (dataToValidate?: Record<string, any>) => {
    if (!metaSchema) {
      return {
        valid: false,
        errors: [
          `Cannot validate ${doctype}: schema is not loaded. Please refresh and ensure your user has permission to read DocType metadata.`,
        ],
      };
    }
    const data = dataToValidate ?? formData;
    return validateDocForm(data, metaSchema, queryClient);
  }, [formData, metaSchema, queryClient]);
  
  // Handle save
  const handleSave = async (dataOverride?: Record<string, any>) => {
    if (!metaSchema) {
      setUiError("Cannot save: metadata not loaded");
      return;
    }

    // Guard: ignore React event objects if accidentally passed
    if (
      dataOverride &&
      ("type" in dataOverride || "nativeEvent" in dataOverride || "_reactName" in dataOverride)
    ) {
      dataOverride = undefined;
    }

    const dataToSave = dataOverride ?? formData;

    // Validate first
    const validation = await validateForm(dataToSave);
    if (!validation.valid) {
      setUiError(`Please fix validation errors:\n${validation.errors.join("\n")}`);
      return;
    }

    // Check if the autoname field was changed — if so, show rename dialog instead of saving
    if (rename.checkForRename(dataToSave)) {
      return;
    }

    // Prepare document for save
    const docToSaveRaw = prepareDocForSave(dataToSave, metaSchema);

    // Prune placeholder child rows
    const docToSave = pruneLocalPlaceholderChildRowsInDoc(
      docToSaveRaw,
      metaSchema,
      (childDoctype) => queryClient.getQueryData<DocTypeSchema>(["meta", childDoctype])
    );

    isSavingRef.current = true;

    try {
      const updatedDoc = isNew
        ? await createMutation.mutateAsync(docToSave)
        : await updateMutation.mutateAsync(docToSave);

      setFormData(updatedDoc);
      setOriginalData(updatedDoc);
      setDocVersion((v) => v + 1);

      if (updatedDoc.name) {
        queryClient.setQueryData<Record<string, any>>(["get", doctype, updatedDoc.name], updatedDoc);
      }

      if (isNew && updatedDoc.name) {
        if (doctype === "Item") captureProductAdded();
        navigate(buildDetailRoute(doctype, updatedDoc.name));
      }
    } catch (error) {
      logger.error("[StandardDetailPage] Save error:", error);

      let errorMessage = "Unknown error";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setUiError(`Failed to save: ${errorMessage}`);
    } finally {
      isSavingRef.current = false;
    }
  };

  // Handle save with patch (for modal submissions)
  const handleSaveWithPatch = async (patch: Record<string, any>) => {
    if (!patch || Object.keys(patch).length === 0) return;

    const prevFormData = formData;
    const mergedData = { ...prevFormData, ...patch };

    // Optimistic UI update (don't touch originalData until server confirms)
    setFormData(mergedData);
    isSavingRef.current = true;

    try {
      const validation = await validateForm(mergedData);
      if (!validation.valid) {
        // Revert optimistic update on validation failure
        setFormData(prevFormData);
        throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
      }
      await handleSave(mergedData);
    } catch (error) {
      // Revert optimistic update on error
      logger.error("[StandardDetailPage] Patch save failed, reverting:", error);
      setFormData(prevFormData);
      throw error;
    } finally {
      isSavingRef.current = false;
    }
  };
  
  // Handle delete — no confirm() here; HeaderActions already shows a confirmation dialog
  const handleDelete = () => {
    if (!docName) return;
    deleteMutation.mutate(docName);
  };

  // Missing doctype param (must be AFTER hooks to satisfy Rules of Hooks)
  if (!doctype) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Missing doctype parameter</div>
        </div>
      </PageContainer>
    );
  }

  // Panel fullscreen mode — render the same GenericDetailContent component
  // that the slide-over/modal panel uses, so the design is identical.
  if (searchParams.get("generic") === "1" && docName) {
    return (
      // Keep the container's normal top padding here: in this direct ?generic=1
      // full-page route the app-shell main has NO top padding, so zeroing it left
      // the sticky top bar's `-mt-4` with nothing to cancel — it over-pulled ~17px
      // above the viewport and clipped at the top edge. The padding gives the bar
      // its intended top inset.
      <PageContainer className="max-w-none">
        <DetailPageTabsProvider>
          <GenericDetailContent doctype={doctype} docName={docName} />
        </DetailPageTabsProvider>
      </PageContainer>
    );
  }

  // Loading state — structural skeleton matching the final UI to prevent layout shift
  if (isLoadingMeta || (isLoadingDoc && !isNew)) {
    return (
      <PageContainer className="max-w-none">
        <DetailPageSkeleton doctype={doctype} />
      </PageContainer>
    );
  }
  
  // Error state — the most common cause is a deep link to a document that belongs
  // to a DIFFERENT store/account than the one currently active (tenant-scoped doc
  // opened from the wrong store), or the document no longer exists. Show a clear,
  // actionable card instead of a bare "Error loading document". TKT-2026-00126.
  if (docError && !isNew) {
    return (
      <DocLoadErrorCard
        currentStore={authStore.tenantCompany}
        onBackToList={() => navigate(buildListRoute(doctype))}
      />
    );
  }

  if (!metaSchema) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Failed to load schema</div>
        </div>
      </PageContainer>
    );
  }
  
  // Page title — prefer title_field value over raw name (which may be a hash)
  const pageTitle: string = isNew
    ? `New ${doctype}`
    : getDocTitle(metaSchema, docData) ?? `${doctype} Detail`;
  
  // Sidebar data for header (image, assignments, shares, etc.)
  const sidebarData = sidebarQuery.data;

  // Doc image from sidebar data or formData
  const docImage = sidebarData?.image || formData?.image || null;

  // Bespoke right sidebar (opt-in) — only on the Details tab of an existing doc,
  // mirroring the Sales Order page's OrderDetailSidebar.
  const showBespokeSidebar =
    !!renderRightSidebar && !isNew && (!activeTab || activeTab === "details");

  // Main view body (System vs Experience) — extracted so it can render either
  // full-width or beside the bespoke sidebar without duplication.
  const detailBody =
    doctype === "Booking" && !isNew ? (
      <BookingDetailContent formData={formData} onRefresh={() => queryClient.invalidateQueries({ queryKey: ["get", doctype, docName] })} />
    ) : doctype === "Booking Resource Config" && !isNew ? (
      <BookingResourceConfigDetailContent formData={formData} onFieldChange={handleFieldChange} />
    ) : resolvedView ? (
      resolvedView.mode === 'system' ? (
        <SystemView
          meta={effectiveMetaSchema!}
          formData={formData}
          onFieldChange={handleFieldChange}
          disabled={!canWrite || createMutation.isPending || updateMutation.isPending || rename.isRenaming || isReadOnly}
          onSave={handleSave}
          onSubmit={handleSaveWithPatch}
          isSaving={createMutation.isPending || updateMutation.isPending || rename.isRenaming}
          resetKey={resetKey}
          documentName={isNew ? undefined : docName}
        />
      ) : resolvedView.view ? (
        <ExperienceView
          view={resolvedView.view}
          meta={effectiveMetaSchema!}
          formData={formData}
          onFieldChange={handleFieldChange}
          disabled={!canWrite || createMutation.isPending || updateMutation.isPending || rename.isRenaming || isReadOnly}
          onSave={handleSave}
          onSubmit={handleSaveWithPatch}
          isSaving={createMutation.isPending || updateMutation.isPending || rename.isRenaming}
          resetKey={resetKey}
          documentName={isNew ? undefined : docName}
          isNew={isNew}
        />
      ) : (
        <div className="text-muted-foreground">No view available</div>
      )
    ) : (
      <div className="text-muted-foreground">Loading view configuration...</div>
    );

  // Render
  return (
    <PageContainer className={
      isNew
        ? "max-w-none"
        : doctype === "Booking Resource Config"
        ? "max-w-none"
        : renderRightSidebar
        ? "max-w-7xl" // Match the Sales Order page width (two-column layout w/ sidebar)
        : "max-w-[940px]"
    }>
      {uiError && (
        <Alert variant="destructive" className="mb-4">
          <button
            type="button"
            onClick={() => setUiError(null)}
            aria-label="Dismiss error"
            className="absolute right-4 top-4 text-destructive/70 hover:text-destructive"
          >
            ×
          </button>
          <AlertDescription>
            <pre className="whitespace-pre-wrap">{uiError}</pre>
          </AlertDescription>
        </Alert>
      )}
      <div className={cn(
        "grid gap-4 sm:gap-6 grid-cols-1",
        showChat ? "items-stretch" : "items-start",
      )}>
        <div
          className={cn(
            showChat ? "relative min-h-0 min-w-0 overflow-hidden" : "relative min-w-0",
            showChat && "md:max-h-[calc(100vh-132px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] lg:max-h-[calc(100vh-88px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]"
          )}
          style={showChat ? {
            height: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            maxHeight: 'calc(100vh - 88px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            overflow: 'hidden',
          } : undefined}
        >
          {/* Sticky top bar (back · title · close) — same chrome as the
              ?generic=1 full-page detail, so every detail page has a consistent
              header top-bar area. */}
          {!isNew && (
            <DetailStickyTopBar
              title={pageTitle}
              onBack={() => navigate(buildListRoute(doctype))}
              onClose={() => navigate(buildListRoute(doctype))}
            />
          )}
          <DetailView
            scrollRef={scrollRef}
            hasStickyHeaderBar={!isNew}
            // PageContainer (md:p-6) is the single source of horizontal padding —
            // mirror the ?generic=1 full-page detail: header + content go
            // edge-to-edge here instead of adding their own px on top.
            contentPaddedExternally
            contentClassName="px-0"
            mediaLayout={headerMediaLayout}
            title={pageTitle}
            titleBadge={
              !isNew && metaSchema?.is_submittable ? (
                <DocStatusBadge docstatus={formData.docstatus ?? 0} />
              ) : undefined
            }
            subtitle={
              !isNew && headerSubtitle ? headerSubtitle({ formData, docName, isNew }) : undefined
            }
            media={
              !isNew && headerMedia
                ? headerMedia({ formData, docName, isNew })
                : !isNew && docImage ? (
                  <HeaderMediaThumbnail
                    image={docImage}
                    video={null}
                    alt={pageTitle}
                  />
                ) : undefined
            }
            backNav={
              // The sticky top bar carries "Back to list" for existing docs, so
              // only show the header back-link on the New form (no top bar there).
              isNew ? (
                <Link
                  to={buildListRoute(doctype)}
                  className="inline-flex items-center gap-0.5 text-sm text-primary hover:text-primary/80 transition-colors -ml-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>{doctype}</span>
                </Link>
              ) : undefined
            }
            actions={
              <HeaderActions
                onSave={handleSave}
                onPermanentDelete={!isNew && canDelete ? handleDelete : undefined}
                isSaving={createMutation.isPending || updateMutation.isPending || rename.isRenaming}
                isDirty={dirtyState}
                allRequiredFilled={allRequiredFilled}
                isNew={isNew}
                itemName={!isNew ? docName : undefined}
                doctype={doctype}
                deleteLabel={doctype.toLowerCase()}
                attachmentCount={sidebarData?.attachments?.length ?? 0}
                onAttachments={!isNew ? () => setShowAttachmentDialog(true) : undefined}
                docstatus={formData.docstatus ?? 0}
                isSubmittable={!!metaSchema?.is_submittable}
                canSubmit={workflow.canSubmit}
                canCancel={workflow.canCancel}
                canAmend={workflow.canAmend}
                onDocSubmit={workflow.handleDocSubmit}
                onDocCancel={workflow.handleDocCancel}
                onDocAmend={workflow.handleDocAmend}
                isSubmitting={workflow.isSubmitting}
                isCancelling={workflow.isCancelling}
                isAmending={workflow.isAmending}
              />
            }
            disableScroll={showChat}
            tabBar={<DetailPageTabBar />}
            belowTitle={
              !isNew && docName ? (
                sidebarData ? (
                  <div className="flex items-center gap-3">
                    <InlineAssignments
                      doctype={doctype}
                      name={docName}
                      assigned_to={sidebarData.assigned_to}
                      editable={canWrite}
                    />
                  </div>
                ) : (
                  /* Reserve space while sidebar loads — matches InlineAssignments h-9 avatar */
                  <Skeleton className="h-9 w-9 rounded-full" />
                )
              ) : undefined
            }
            belowActions={
              !isNew && docName ? (
                sidebarData ? (
                  <InlineShareActions
                    doctype={doctype}
                    name={docName}
                    shares={sidebarData.shares}
                    public_share={sidebarData.public_share}
                    share_capabilities={sidebarData.share_capabilities}
                    liked_by={sidebarData.liked_by}
                    like_count={sidebarData.like_count}
                    comment_count={sidebarData.comment_count}
                    followed={sidebarData.is_followed}
                    tagCount={sidebarData.tags?.length ?? 0}
                    onTagClick={() => setShowTagModal(true)}
                    editable={canWrite}
                    onComment={() => setShowChat(!showChat)}
                  />
                ) : (
                  /* Reserve space while sidebar loads — matches InlineShareActions h-9 pill bar */
                  <Skeleton className="h-9 w-[340px] rounded-full" />
                )
              ) : undefined
            }
          >
            {showChat ? (
              !isNew && docName ? (
                <div className="h-full w-full flex min-h-0">
                  <DocChatPanel
                    doctype={doctype}
                    name={docName}
                    onClose={() => setShowChat(false)}
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">
                  Chat is only available after the document is created.
                </div>
              )
            ) : showBespokeSidebar ? (
              <div className="flex flex-col xl:flex-row gap-6">
                <div className="flex-1 min-w-0 space-y-6">
                  {renderAboveMain && renderAboveMain({ formData, docName, isNew })}
                  {detailBody}
                  {renderBelowMain && renderBelowMain({ formData, docName, isNew })}
                </div>
                <div className="w-full xl:w-[360px] shrink-0 order-first xl:order-last">
                  {renderRightSidebar!({ formData, docName, isNew })}
                </div>
              </div>
            ) : (
              detailBody
            )}
          </DetailView>

          {/* Attachment list modal */}
          {!isNew && docName && (
            <AttachmentListModal
              open={showAttachmentDialog}
              onOpenChange={setShowAttachmentDialog}
              doctype={doctype}
              name={docName}
              fallbackAttachments={sidebarData?.attachments}
            />
          )}

          {/* Tag list modal */}
          {!isNew && docName && (
            <TagListModal
              open={showTagModal}
              onOpenChange={setShowTagModal}
              doctype={doctype}
              name={docName}
              tags={sidebarData?.tags ?? []}
            />
          )}
        </div>
      </div>

      {/* Rename confirmation dialog — shown when autoname field is edited and user saves */}
      <RenameConfirmDialog
        open={rename.renameDialogOpen}
        onOpenChange={(open) => { if (!open) rename.handleRenameCancel(); }}
        onConfirm={() => rename.handleRenameConfirm(formData, originalData)}
        doctype={doctype}
        oldName={rename.renameOldName}
        newName={rename.renameNewName}
      />
    </PageContainer>
  );
}
